import { useEffect, useState } from 'react';
import type { DevSettings } from '../shared/dev.ts';
import { adminApi, type DevEnvHints, type DevInherited } from './api.ts';

/** Champs qui peuvent rester hérités de l'environnement plutôt que surchargés. */
type Inheritable = keyof DevInherited;

const INHERITABLE: Inheritable[] = [
  'geminiModels',
  'publicBaseUrl',
  'hotFolder',
  'photoSaveFolder',
  'printerStatusCommand',
];

const isSet = (value: string | string[]) => (Array.isArray(value) ? value.length > 0 : value !== '');

/**
 * Paramètres d'installation.
 *
 * Les champs s'ouvrent sur ce qui s'applique déjà — cascade de modèles du thème,
 * URL déduite du réseau — plutôt que sur des cases vides. Mais afficher n'est
 * pas surcharger : tant qu'on n'a pas touché un champ, il reste *hérité* et
 * n'est pas enregistré. Sans cette distinction, ouvrir l'onglet et enregistrer
 * figerait l'URL publique sur l'adresse IP du jour, et la borne ne suivrait plus
 * le réseau du salon suivant.
 *
 * Rien n'est écrit au fil de la frappe, contrairement à l'onglet client : une
 * clé d'API ou un chemin d'impression à moitié saisis casseraient la borne en
 * pleine séance.
 */
export function DevSection({ onExit }: { onExit: () => void }) {
  const [draft, setDraft] = useState<DevSettings | null>(null);
  const [inherited, setInherited] = useState<DevInherited | null>(null);
  const [env, setEnv] = useState<DevEnvHints | null>(null);
  const [overridden, setOverridden] = useState<Set<Inheritable>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error' | 'busy'; text: string } | null>(
    null,
  );
  const [showKey, setShowKey] = useState(false);
  const [showTunnel, setShowTunnel] = useState(false);

  useEffect(() => {
    adminApi
      .loadDev()
      .then((state) => {
        const marks = new Set<Inheritable>(INHERITABLE.filter((key) => isSet(state.settings[key])));
        setOverridden(marks);
        // Un champ jamais surchargé s'affiche avec la valeur héritée.
        setDraft({
          ...state.settings,
          ...Object.fromEntries(
            INHERITABLE.filter((key) => !marks.has(key)).map((key) => [key, state.inherited[key]]),
          ),
        });
        setInherited(state.inherited);
        setEnv(state.env);
      })
      .catch((error: Error) => setMessage({ kind: 'error', text: error.message }));
  }, []);

  if (!draft || !inherited) {
    return (
      <section className="card">
        <p className="card__empty">{message?.text ?? 'Chargement des réglages…'}</p>
      </section>
    );
  }

  const set = (partial: Partial<DevSettings>) => {
    setDraft({ ...draft, ...partial });
    setDirty(true);
    setMessage(null);
  };

  /** Une saisie dans un champ héritable le fait passer en surcharge. */
  const override = (key: Inheritable, value: string | string[]) => {
    setOverridden(new Set(overridden).add(key));
    set({ [key]: value } as Partial<DevSettings>);
  };

  const restore = (key: Inheritable) => {
    const marks = new Set(overridden);
    marks.delete(key);
    setOverridden(marks);
    set({ [key]: inherited[key] } as Partial<DevSettings>);
  };

  const save = async () => {
    setMessage({ kind: 'busy', text: 'Enregistrement…' });
    try {
      // Un champ resté hérité est renvoyé vide : c'est ainsi que le serveur sait
      // qu'il doit continuer à suivre l'environnement.
      const payload: Partial<DevSettings> = { ...draft };
      for (const key of INHERITABLE) {
        if (!overridden.has(key)) {
          (payload as Record<string, unknown>)[key] = key === 'geminiModels' ? [] : '';
        }
      }

      const { settings } = await adminApi.saveDev(payload);
      setDraft({
        ...settings,
        ...Object.fromEntries(
          INHERITABLE.filter((key) => !overridden.has(key)).map((key) => [key, inherited[key]]),
        ),
      });
      setDirty(false);
      setMessage({
        kind: 'ok',
        text: 'Enregistré — la borne l’applique dès réception, sans redémarrage.',
      });
    } catch (error) {
      setMessage({ kind: 'error', text: (error as Error).message });
    }
  };

  /** Pastille de provenance, pour ne pas confondre « affiché » et « enregistré ». */
  const Origin = ({ field }: { field: Inheritable }) =>
    overridden.has(field) ? (
      <button type="button" className="origin origin--set" onClick={() => restore(field)}>
        surchargé — rétablir
      </button>
    ) : isSet(inherited[field]) ? (
      <span className="origin">hérité</span>
    ) : null;

  return (
    <>
      <section className="card card--dev">
        <header>
          <h2>Paramètres avancés — installation</h2>
          <button type="button" className="btn btn--ghost btn--small" onClick={onExit}>
            Masquer ces paramètres
          </button>
        </header>

        <p className="card__hint">
          Les champs s’ouvrent sur ce qui s’applique déjà. Tant qu’on n’y touche pas, ils restent
          <strong> hérités</strong> du <code>.env</code> de la borne et du thème et ne sont pas
          enregistrés — l’URL publique continue par exemple de suivre le réseau. Une saisie les fait
          passer en surcharge, envoyée à la borne par Firestore, qui l’emporte alors sur le reste.
        </p>

        <div className="field-grid">
          <label className="field--wide">
            Clé API Gemini
            <div className="field-row">
              <input
                type={showKey ? 'text' : 'password'}
                value={draft.geminiApiKey}
                placeholder={
                  env?.geminiApiKey
                    ? `Clé du .env en place (${env.geminiApiKey}) — saisir ici pour la remplacer`
                    : 'Aucune clé dans .env'
                }
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => set({ geminiApiKey: e.target.value })}
              />
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setShowKey((v) => !v)}
              >
                {showKey ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            <span className="field__note">
              Jamais préremplie ni renvoyée en clair : seul son préfixe sort du serveur.
            </span>
          </label>

          <label className="field--wide">
            Lien des QR codes
            <select
              value={draft.shareMode}
              onChange={(e) => set({ shareMode: e.target.value as DevSettings['shareMode'] })}
            >
              <option value="admin">Photo envoyée à l’admin en ligne (conseillé)</option>
              <option value="borne">Photo servie par la borne (tunnel ou Wi-Fi du stand)</option>
            </select>
            <span className="field__note">
              « Admin en ligne » : la borne envoie chaque photo, le QR code ouvre une page de cette
              admin, sans connexion. Marche derrière un proxy d’entreprise, sans tunnel. Photos
              visibles dans l’onglet Photos, effacées au bout de 7 jours.
            </span>
          </label>

          <label className="field--wide">
            Jeton du tunnel Cloudflare
            <div className="field-row">
              <input
                type={showTunnel ? 'text' : 'password'}
                value={draft.tunnelToken}
                placeholder={
                  env?.tunnelToken
                    ? `Jeton du .env en place (${env.tunnelToken}) — saisir ici pour le remplacer`
                    : 'Aucun jeton : pas de tunnel, le QR code ne marche que sur le Wi-Fi du stand'
                }
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => set({ tunnelToken: e.target.value.trim() })}
              />
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => setShowTunnel((v) => !v)}
              >
                {showTunnel ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            <span className="field__note">
              Cloudflare Zero Trust &gt; Networks &gt; Tunnels &gt; le tunnel de la borne &gt;
              « Install connector » : la longue chaîne après <code>--token</code>. L’application de
              la borne démarre le tunnel elle-même ; sa route publique doit pointer vers{' '}
              <code>http://localhost:8787</code>.
            </span>
          </label>

          <label className="field--wide">
            <span className="field__label">
              Modèles Gemini — un par ligne, essayés dans l’ordre
              <Origin field="geminiModels" />
            </span>
            <textarea
              rows={3}
              value={draft.geminiModels.join('\n')}
              spellCheck={false}
              onChange={(e) =>
                override(
                  'geminiModels',
                  e.target.value
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean),
                )
              }
            />
            <span className="field__note">
              Cascade du thème {env?.themeId ? <code>{env.themeId}</code> : null} tant qu’elle n’est
              pas surchargée.
            </span>
          </label>

          <label className="field--wide">
            <span className="field__label">
              URL publique du QR code — tunnel Cloudflare
              <Origin field="publicBaseUrl" />
            </span>
            <input
              value={draft.publicBaseUrl}
              spellCheck={false}
              onChange={(e) => override('publicBaseUrl', e.target.value)}
            />
            <span className="field__note">
              Héritée, elle suit l’adresse du réseau ({env?.publicBaseUrl}). Surchargée, elle est
              figée — ce qu’il faut pour un tunnel, jamais pour une IP de salon.
            </span>
          </label>

          <label className="field--wide">
            <span className="field__label">
              Hot folder d’impression
              <Origin field="hotFolder" />
            </span>
            <input
              value={draft.hotFolder}
              placeholder={
                env?.printerName
                  ? `Vide = impression CUPS sur ${env.printerName}`
                  : 'Vide = impression CUPS (lp), imprimante par défaut'
              }
              spellCheck={false}
              onChange={(e) => override('hotFolder', e.target.value)}
            />
          </label>

          <label className="field--wide">
            <span className="field__label">
              Dossier de sauvegarde des photos — optionnel
              <Origin field="photoSaveFolder" />
            </span>
            <input
              value={draft.photoSaveFolder}
              placeholder={`Vide = pas de copie hors de ${env?.capturesDir ?? 'captures/'}`}
              spellCheck={false}
              onChange={(e) => override('photoSaveFolder', e.target.value)}
            />
          </label>

          <label className="field--wide">
            <span className="field__label">
              Commande d’état de l’imprimante — optionnel
              <Origin field="printerStatusCommand" />
            </span>
            <input
              value={draft.printerStatusCommand}
              placeholder="Vide = interrogation CUPS, sinon compteur interne"
              spellCheck={false}
              onChange={(e) => override('printerStatusCommand', e.target.value)}
            />
          </label>

          <label>
            Resynchronisation des thèmes — {draft.themeSyncMinutes || 'désactivée'}
            {draft.themeSyncMinutes ? ' min' : ''}
            <input
              type="number"
              min={0}
              max={1440}
              value={draft.themeSyncMinutes}
              onChange={(e) => set({ themeSyncMinutes: Number(e.target.value) })}
            />
          </label>

          <label>
            Durée de l’écran de génération — {draft.processingSeconds} s
            <input
              type="number"
              min={3}
              max={180}
              value={draft.processingSeconds}
              onChange={(e) => set({ processingSeconds: Number(e.target.value) })}
            />
          </label>
        </div>

        <p className="card__hint card__hint--footnote">
          La durée de génération ne coupe rien : elle cadence la barre de progression pendant
          l’attente réelle, à réajuster quand le modèle change. La commande d’état doit imprimer le
          nombre de tirages restants sur sa sortie standard ; elle est exécutée sans shell. La
          resynchronisation relit la bibliothèque de packs à intervalle régulier — la borne recharge
          alors son thème à l’accueil, jamais en pleine séance ; 0 désarme.
        </p>

        <div className="card__actions card__actions--end">
          {message && <span className={`pill pill--${message.kind}`}>{message.text}</span>}
          <button type="button" className="btn" disabled={!dirty} onClick={() => void save()}>
            {dirty ? 'Enregistrer' : 'Enregistré'}
          </button>
        </div>
      </section>

      <section className="card">
        <header>
          <h2>Ce qui s’applique</h2>
        </header>
        <dl className="diag">
          <div>
            <dt>Clé Gemini</dt>
            <dd>
              {draft.geminiApiKey
                ? 'réglée ici'
                : env?.geminiApiKey
                  ? `.env (${env.geminiApiKey})`
                  : '⚠️ absente'}
            </dd>
          </div>
          <div>
            <dt>Modèles</dt>
            <dd>{draft.geminiModels.join(' → ') || '—'}</dd>
          </div>
          <div>
            <dt>URL du QR code</dt>
            <dd>{draft.publicBaseUrl || '—'}</dd>
          </div>
          <div>
            <dt>Impression</dt>
            <dd>
              {draft.hotFolder
                ? `hot folder — ${draft.hotFolder}`
                : `CUPS (lp) — ${env?.printerName || 'imprimante par défaut'}`}
            </dd>
          </div>
          <div>
            <dt>Sauvegarde locale</dt>
            <dd>{draft.photoSaveFolder || 'désactivée'}</dd>
          </div>
          <div>
            <dt>Niveau de média</dt>
            <dd>{draft.printerStatusCommand || 'CUPS, sinon compteur interne'}</dd>
          </div>
          <div>
            <dt>Resynchro des thèmes</dt>
            <dd>
              {draft.themeSyncMinutes ? `toutes les ${draft.themeSyncMinutes} min` : 'désactivée'}
            </dd>
          </div>
          <div>
            <dt>Dernière modification</dt>
            <dd>{draft.updatedAt ? new Date(draft.updatedAt).toLocaleString('fr-FR') : 'jamais'}</dd>
          </div>
        </dl>
      </section>
    </>
  );
}
